import React, { useRef, useState } from "react";
import { HttpStatusCode } from "axios";

import useErrors from "../../../libs/useErrors";

import { AtgWorker, searchAtgWorker } from "../../../services/atgServices";

import useFullScreenLoading from "../../../hooks/useFullScreenLoading";

import CommonInput from "../../Input/CommonInput";
import PrimaryButton from "../../Button/Primary";
import CommonCheckbox from "../../Input/Checkbox";
import HorizontalRule from "../../Rule/Horizontal";

import ZoomSearchIcon from "../../../svgs/icons/ZoomSearchIcon";
import { NoDataIcon } from "../../../pages/Center";

type Props = {
  atgBuildingId: string;
  onChange?: (worker: AtgWorker[]) => void;
  onClose?: () => void;
  prevSelectedWorker?: AtgWorker[];
};

export default function FindWorker({
  atgBuildingId,
  onChange,
  onClose,
  prevSelectedWorker,
}: Props) {
  const workerRef = useRef<HTMLInputElement>(null);

  const [errors, { removeError, setError }] = useErrors();

  const [workerName, setWorkerName] = useState("");
  const [selectedWorker, setSelectedWorker] = useState<AtgWorker[]>(
    prevSelectedWorker ?? []
  );
  const [workerList, setWorkerList] = useState<AtgWorker[]>([]);
  const [isSearch, setIsSearch] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const loadingComponent = useFullScreenLoading({ isLoading });

  const searchWorker = () => {
    if (workerName) {
      setIsLoading(true);
      searchAtgWorker({ atgBuildingId, workerName })
        .then((res) => {
          if (res.status === HttpStatusCode.Ok) {
            setWorkerList(res.data);
            setIsSearch(true);
          }
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      setError("worker", "작업자 이름은 필수입니다.");
      workerRef.current?.focus();
    }
  };

  const isWorkerSelected = (
    selected: AtgWorker,
    worker: AtgWorker
  ): boolean => {
    return (
      selected.workerName === worker.workerName && selected.job === worker.job
    );
  };

  return (
    <div className="flex flex-col gap-5">
      {loadingComponent}
      <div className="px-5">
        <CommonInput
          placeholder="작업자 이름을 입력해주세요"
          className="p-4 text-base w-full bg-brand-neutral-05"
          postfix={{
            content: (
              <div
                onClick={() => {
                  searchWorker();
                }}
              >
                <ZoomSearchIcon />
              </div>
            ),
            className: "px-3",
          }}
          value={workerName}
          error={errors["worker"]}
          onChange={(value: string) => {
            setWorkerName(value);
            removeError("worker");
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              searchWorker();
            }
          }}
          ref={workerRef}
        />
      </div>

      {isSearch &&
        (workerList && workerList.length > 0 ? (
          <div className="px-5 max-h-[50vh] overflow-scroll">
            {workerList.map((worker, index) => (
              <React.Fragment key={index}>
                <HorizontalRule margin="0" />
                <div
                  key={worker.job + worker.workerName}
                  className="px-2.5 flex gap-2.5 items-center h-[60px]"
                  onClick={() => {
                    if (
                      selectedWorker.some((selected) =>
                        isWorkerSelected(selected, worker)
                      )
                    ) {
                      setSelectedWorker(
                        selectedWorker.filter(
                          (item) => !isWorkerSelected(item, worker)
                        )
                      );
                    } else {
                      setSelectedWorker((prev) => [...prev, worker]);
                    }
                  }}
                >
                  <CommonCheckbox
                    checked={selectedWorker.some((selected) =>
                      isWorkerSelected(selected, worker)
                    )}
                  />
                  <div>
                    {worker.workerName}{" "}
                    <span className=" text-brand-neutral-60">
                      ({worker.job})
                    </span>
                  </div>
                </div>
              </React.Fragment>
            ))}
          </div>
        ) : (
          <div className="sticky top-[72px] w-full h-40 flex flex-col items-center justify-center gap-2">
            <NoDataIcon />
            조회 결과가 없어요
          </div>
        ))}

      <PrimaryButton
        className="text-white h-[50px] w-full flex justify-center items-center text-2xl font-bold"
        onClick={() => {
          if (!selectedWorker || selectedWorker.length === 0) return;
          onChange?.(selectedWorker);
          onClose?.();
        }}
        disabled={!selectedWorker || selectedWorker.length === 0}
      >
        추가
      </PrimaryButton>
    </div>
  );
}
